import { animate, style, transition, trigger } from "@angular/animations";
import { BreakpointState } from "@angular/cdk/layout";
import { Platform } from "@angular/cdk/platform";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";

import {
    ControlPosition,
    HIGH_POSITION,
    MODERATE_POSITION,
    VideoComponent,
} from "@hermes/aphrodite/video";
import { Asset } from "@hermes/api-model-asset";
import { Product, ProductTemplateType } from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";

import {
    DEFAULT_MAX_VISIBLE_IMAGES_ON_PRODUCT_PAGE,
    PRODUCT_PAGE_IMAGE_SIZE,
} from "@hermes/fragments/product-utils";
import {
    BELTKIT,
    SMALL_LEATHER_GOODS,
} from "@hermes/states/personalization-page";
import { ProductPageFacade } from "@hermes/states/product-page";
import { AnalyticsService } from "@hermes/utils/analytics";

import { AssetMediaType } from "@hermes/utils-generic/constants";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

import { ProductContentChangeImageEvent } from "app/pages/product-page/events/product-content-change-image.event";

import { AssetData } from "../product-image-gallery/product-image-gallery.component";

@Component({
    selector: "h-product-image",
    templateUrl: "./product-image.component.html",
    styleUrls: ["./product-image.component.scss"],
    animations: [
        trigger("fadingAnimation", [
            transition("hidden => shown", [
                style({ opacity: 0 }),
                animate("0.75s ease-in-out", style({ opacity: 1 })),
            ]),
            transition(":enter", [
                style({ opacity: 0 }),
                animate("0.75s ease-in-out", style({ opacity: 1 })),
            ]),
        ]),
        // disable initial SSR animation
        trigger("imageNoop", [transition("* => *", [])]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductImageComponent implements OnInit, OnDestroy {
    /**
     * Is VTO enabled
     */
    @Input()
    public isVtoEnabled: boolean = false;

    @Output()
    public imageChanged: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild(VideoComponent)
    public videoComponent: VideoComponent;

    public isMobile: boolean;

    /**
     * current index
     */
    public currentIndex = 0;

    /**
     * by default we only display 5 images
     */
    public defaultMaxVisibleImages = DEFAULT_MAX_VISIBLE_IMAGES_ON_PRODUCT_PAGE;
    /**
     * If only one remainingImages, we must display one more image than defaultMaxVisibleImages.
     */
    public maxVisibleImages = DEFAULT_MAX_VISIBLE_IMAGES_ON_PRODUCT_PAGE;

    /**
     * number displayed in show more button
     */
    public remainingImages: number;
    /**
     * Size of the square image
     */
    public thumbnailSize = 112;

    /**
     * Product title. use in translation key for image alt
     */
    public productTitle: string;

    /** Observable stream */
    public subscription: Subscription = new Subscription();

    public shouldDisplayApwAnimations: boolean;

    public size = PRODUCT_PAGE_IMAGE_SIZE;

    public isImageLoaded: boolean = false;

    public compositeAsset = "";

    public isPersoProduct: boolean = false;

    public moderatePosition: ControlPosition = MODERATE_POSITION;
    public highPosition: ControlPosition = HIGH_POSITION;

    private inputProduct: Product = new Product();

    constructor(
        private readonly context: Context,
        private analyticsService: AnalyticsService,
        private productPageFacade: ProductPageFacade,
        private platform: Platform,
        private changeDetector: ChangeDetectorRef,
        private breakpointService: BreakpointService,
    ) {}

    @Input()
    public set moveTo(value: number) {
        this.currentIndex = value;
    }

    /**
     * current product
     */
    @Input()
    public set product(product: Product) {
        this.inputProduct = product;
        if (product) {
            this.productTitle = product.title;
            this.isPersoProduct = [SMALL_LEATHER_GOODS, BELTKIT].includes(
                this.inputProduct.persoProductType,
            );
            this.calculateShouldDisplayApwAnimations();
            this.isImageLoaded = false;
            this.remainingImages =
                this.inputProduct.assets?.length > this.defaultMaxVisibleImages
                    ? this.inputProduct.assets?.length -
                      this.defaultMaxVisibleImages
                    : 0;
            this.maxVisibleImages =
                this.remainingImages === 1
                    ? this.defaultMaxVisibleImages + 1
                    : this.defaultMaxVisibleImages;
            this.compositeAsset = this.getFirstCompositeImageForGallery(
                this.inputProduct.assets,
            );
        }
    }
    public get product() {
        return this.inputProduct;
    }

    public ngOnInit(): void {
        this.currentIndex = 0;
        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        this.isMobile = !value.matches;
                        this.changeDetector.detectChanges();
                    }),
            );
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * navigate to the image that corresponds to the index
     */
    public changeImage(eventData: [AssetData, boolean]): void {
        const [assetData, sendEventAnalytic] = eventData;

        if (this.currentIndex !== assetData.index) {
            this.isImageLoaded = false;
        }
        this.currentIndex = assetData.index;
        this.imageChanged.emit(this.currentIndex);
        this.calculateShouldDisplayApwAnimations();

        if (!this.isPersoProduct && sendEventAnalytic) {
            this.analyticsService.sendData(
                new ProductContentChangeImageEvent({
                    index: assetData.index,
                    templateType: this.inputProduct.templateType,
                    type: assetData.type,
                    sku: this.inputProduct.sku,
                }),
            );
        }
    }

    /**
     * generate aria-labelledby dynamicly with index
     *
     * @param index current image index
     */
    public prefixerLabelledBy(index: number): string {
        return `tab${index}`;
    }

    public handleFocus(): void {
        if (this.context.isInBrowserMode()) {
            window.scrollTo(0, 0);
        }
    }

    /**
     * handle zoom activation
     */
    public handleZoomModal(zoomStatus: boolean): void {
        this.productPageFacade.toggleZoom({
            zoomStatus,
            currentIndex: this.currentIndex,
            zoomAssets: this.inputProduct?.assets,
            isTopLevelProduct: true,
            templateType: this.inputProduct?.templateType,
        });
    }

    public getMediaState(index: number, asset?: Asset): string {
        const isVideo = asset?.type === "video";
        if (this.currentIndex === index) {
            return `shown${isVideo ? " shown-video" : ""}`;
        }
        return "hidden";
    }

    /**
     * Method to rerender only differents assets into ngFor directive
     * Must be arrow function to keep context
     */
    public trackByAsset = (index: number, item: Asset): string | number => {
        if (this.shouldEscapeFromAngularDetection(index)) {
            return index;
        }
        return `${index}_${item.url}`;
    };

    /**
     * APW animations are only on
     * - first image
     * - animations are available
     */
    public calculateShouldDisplayApwAnimations(): void {
        this.shouldDisplayApwAnimations =
            this.currentIndex === 0 && this.areAnimationsAvailable();
    }

    /**
     * Take care with that is not a very common practice
     * We need leave angular re-rendering of assets loop to
     * - let apw animation take care about picture rendering
     * - no picture flash on variation change
     * Return true if :
     * - is first index of ng for
     * - animations are available
     * - is perso product
     */
    public shouldEscapeFromAngularDetection(ngForIndex: number): boolean {
        return (
            (ngForIndex === 0 && this.areAnimationsAvailable()) ||
            this.isPersoProduct
        );
    }

    /*
     * Return true if
     * - we are on APW template
     * - it's not IE
     */
    public areAnimationsAvailable(): boolean {
        return (
            this.inputProduct?.templateType ===
                ProductTemplateType.AppleWatch && !this.platform.TRIDENT
        );
    }

    public onImageLoaded(): void {
        this.isImageLoaded = true;
    }

    private getFirstCompositeImageForGallery(assets: Asset[]): string {
        if (assets?.length) {
            const compositeAsset = assets.find(
                (asset: Asset) =>
                    asset.type === AssetMediaType.Image &&
                    asset.url.includes("composite_"),
            );

            if (compositeAsset?.url !== undefined) {
                return this.getPositionCompositeAssetFromAssetUrl(
                    compositeAsset.url,
                );
            }
        }

        return "";
    }

    private getPositionCompositeAssetFromAssetUrl(
        url: string,
    ): string | undefined {
        const matchedComposite = url.match(/composite_\d+(?!.*\d)/);
        return matchedComposite && matchedComposite?.length > 0
            ? matchedComposite[0]
            : undefined;
    }
}
